/**
 * F12.4 — DRILL-DOWN DE LOS INDICADORES DE MANTENIMIENTO
 *
 * Los listados (partes, incidencias y partes del portal) reciben en la URL el filtro EXACTO de la cifra de la que
 * se viene (`indicador`, su periodo `pDesde`/`pHasta`, motivo, ubicación…) y `volver`: la URL de los indicadores,
 * con su periodo y filtros. Aquí están los textos de cada indicador y el aviso que lo enseña: «← Volver a
 * Indicadores» y un aviso quitable por filtro, para que siempre se vea por qué el listado muestra lo que muestra.
 */
import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { fecha } from './base.tsx';

/** Un decimal, coma decimal, sin «,0» sobrante: 42 → «42», 28.6 → «28,6». */
export function num(v: number): string {
  return v.toFixed(1).replace('.', ',').replace(/,0$/, '');
}
/** Horas legibles: «menos de 1 h», «36 h», «13,5 días»; «—» si no hay datos. */
export function textoHoras(h: number | null): string {
  if (h === null) return '—';
  if (h < 1) return 'menos de 1 h';
  return h >= 48 ? `${num(h / 24)} días` : `${num(h)} h`;
}
/** Porcentaje con coma decimal; «—» si no hay denominador (nunca 0 %). */
export function textoCociente(c: { pct: number | null }): string {
  return c.pct === null ? '—' : `${num(c.pct)} %`;
}

/** Texto de cada indicador de partes (el periodo se añade aparte). */
export const TEXTO_INDICADOR_PT: Readonly<Record<string, string>> = {
  ACTIVOS: 'Activos ahora',
  ABIERTOS: 'Sin planificar',
  EN_CURSO: 'En curso',
  PENDIENTES: 'Pendientes',
  REABIERTOS: 'Reabiertos ahora',
  ATRASADOS: 'Atrasados',
  SIN_TECNICO: 'Sin técnico',
  POR_CERRAR: 'Finalizados, pendientes de cierre',
  PREVENTIVOS_VENCIDOS: 'Preventivos vencidos sin hacer',
  CREADOS: 'Creados',
  CERRADOS: 'Cerrados',
  REABIERTOS_PERIODO: 'Reabiertos',
  PREV_PREVISTOS: 'Preventivos previstos',
  PREV_EJECUTADOS: 'Preventivos hechos',
  PREV_VENCIDOS: 'Preventivos vencidos',
  PREV_POR_LLEGAR: 'Preventivos por llegar',
  PREV_ANULADOS: 'Preventivos anulados',
};
/** Los del área del cliente, en sus palabras (nunca «sin técnico», «atrasado» ni motivos internos). */
export const TEXTO_INDICADOR_PT_PUBLICO: Readonly<Record<string, string>> = {
  ACTIVOS: 'En marcha',
  CREADOS: 'Abiertos',
  CERRADOS: 'Cerrados',
  PREV_PREVISTOS: 'Mantenimientos preventivos previstos',
  PREV_EJECUTADOS: 'Mantenimientos preventivos hechos',
  PREV_VENCIDOS: 'Mantenimientos preventivos pendientes de hacer',
  PREV_POR_LLEGAR: 'Mantenimientos preventivos por llegar',
};
export const TEXTO_INDICADOR_IM: Readonly<Record<string, string>> = {
  IM_ABIERTAS: 'Abiertas o en revisión',
  IM_SIN_PT: 'Sin parte de trabajo',
  IM_CON_PT_ACTIVO: 'Con un parte en marcha',
  IM_POR_RESOLVER: 'Con todo su trabajo terminado (por resolver)',
  IM_CREADAS: 'Registradas',
  IM_RESUELTAS: 'Resueltas',
  IM_DEVUELTAS: 'Devueltas a revisión',
};
export const TEXTO_MOTIVO: Readonly<Record<string, string>> = {
  ESPERA_REPUESTO: 'Esperando repuesto',
  ESPERA_CLIENTE: 'Esperando al cliente',
  ESPERA_PROVEEDOR: 'Esperando al proveedor',
  NUEVA_VISITA: 'Necesita otra visita',
  OTRO: 'Otro motivo',
};

/** Las claves de URL del drill-down (además de las de cada listado). */
export const CLAVES_DRILL = ['indicador', 'pDesde', 'pHasta', 'motivoPendiente', 'idUbicacion', 'ubicacion', 'idCliente', 'cliente', 'tecnicoNombre', 'volver'] as const;
export type ClaveDrill = (typeof CLAVES_DRILL)[number];
export const DRILL_VACIO: Record<ClaveDrill, string> = {
  indicador: '', pDesde: '', pHasta: '', motivoPendiente: '', idUbicacion: '', ubicacion: '', idCliente: '', cliente: '', tecnicoNombre: '', volver: '',
};

export interface Chip { readonly clave: string; readonly texto: string; readonly quitar: Partial<Record<string, string>> }

/** Avisos de los filtros del drill-down presentes en la URL (`valor` lee la URL del listado). */
export function chipsDrill(valor: (k: ClaveDrill) => string, textos: Readonly<Record<string, string>>): Chip[] {
  const chips: Chip[] = [];
  const ind = valor('indicador');
  if (ind !== '') {
    const per = valor('pDesde') !== '' ? ` del ${fecha(valor('pDesde'))} al ${fecha(valor('pHasta'))}` : '';
    chips.push({ clave: 'i', texto: `${textos[ind] ?? ind}${per}`, quitar: { indicador: '', pDesde: '', pHasta: '' } });
  }
  if (valor('motivoPendiente') !== '') {
    chips.push({ clave: 'm', texto: `Motivo: ${TEXTO_MOTIVO[valor('motivoPendiente')] ?? valor('motivoPendiente')}`, quitar: { motivoPendiente: '' } });
  }
  if (valor('idUbicacion') !== '') {
    const u = valor('idUbicacion') === 'SIN' ? 'sin ubicación' : valor('ubicacion') || 'elegida';
    chips.push({ clave: 'u', texto: `Ubicación (cuando se abrió): ${u}`, quitar: { idUbicacion: '', ubicacion: '' } });
  }
  if (valor('idCliente') !== '') chips.push({ clave: 'c', texto: `Cliente: ${valor('cliente') || 'elegido'}`, quitar: { idCliente: '', cliente: '' } });
  return chips;
}

/**
 * F13.7: `volver` llega en la URL (la puede fabricar cualquiera): sólo se acepta una ruta INTERNA de la aplicación
 * («/…», nunca «//host», «https://…», «javascript:…» ni barras invertidas). Si no, no hay enlace de vuelta.
 */
export function rutaInterna(v: string): string {
  return /^\/(?![/\\])/.test(v) && !/[\\\s]/.test(v) ? v : '';
}

/** «← Volver a Indicadores» (si se vino de ellos) y los filtros que trajo el indicador, cada uno quitable. */
export function AvisoDrill({ volver: pedido, chips, quitar }: { volver: string; chips: readonly Chip[]; quitar: (c: Chip) => void }): ReactNode {
  const volver = rutaInterna(pedido);
  if (volver === '' && chips.length === 0) return null;
  return (
    <div className="aviso-drill">
      {volver !== '' && <p style={{ marginTop: 0 }}><Link to={volver} className="boton boton-tactil">← Volver a Indicadores</Link></p>}
      {chips.length > 0 && (
        <div className="fila chips-drill" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 10 }} role="group" aria-label="Filtros desde Indicadores">
          {chips.map((c) => (
            <button key={c.clave} type="button" className="chip-filtro" onClick={() => quitar(c)} aria-label={`Quitar filtro: ${c.texto}`}>
              {c.texto} <span aria-hidden="true">✕</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Construye el enlace a un listado con su filtro y la vuelta a ESTA pantalla (con su periodo y filtros). */
export function useDrillMnt(): (ruta: string, filtros: Record<string, string>) => string {
  const l = useLocation();
  const volver = `${l.pathname}${l.search}`;
  return (ruta, filtros) => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(filtros)) if (v !== '') q.set(k, v);
    q.set('volver', volver);
    return `${ruta}?${q.toString()}`;
  };
}
